import { useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { XMarkIcon } from "@heroicons/react/24/outline";
import axios from "../../../api/axios";
import { resolveDrawingPdfUrl } from "../drawings/drawingPdfUtils";
import { fetchProjectDrawings } from "../drawings/drawingStructureApi";
import {
  alignableStoreys,
  fetchPdfBytes,
  linkDrawingPdfErrorKey,
  overlayToControlPoints,
  rasterizePdfPage,
} from "./ifc-drawing-align";

if (!pdfjs.GlobalWorkerOptions.workerSrc) {
  pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
}

/**
 * «Koble tegning»: pick drawing + non-empty datum storey, then drag the
 * rasterized PDF as a plane in the main 3D viewer. Two-click pairs stay as
 * an advanced fallback.
 */
export function IfcLinkDrawingDialog({
  t,
  open,
  projectId,
  drawings: drawingsProp,
  initialDrawingId,
  storeys = [],
  overlayTransform,
  saving,
  onStoreyPicked,
  onOverlayStart,
  onOverlayUpdate,
  onOverlayClear,
  onIfcPointWait,
  onSave,
  onClose,
}) {
  const [step, setStep] = useState("pick");
  const [drawings, setDrawings] = useState(drawingsProp || []);
  const [drawingsLoading, setDrawingsLoading] = useState(false);
  const [drawingId, setDrawingId] = useState(initialDrawingId || "");
  const [storeyId, setStoreyId] = useState("");
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfError, setPdfError] = useState(null);
  const [pageSize, setPageSize] = useState({ width: 1, height: 1 });
  const [overlayPage, setOverlayPage] = useState({ width: 1, height: 1 });
  const [opacity, setOpacity] = useState(0.72);
  const [rasterizing, setRasterizing] = useState(false);
  const [pairs, setPairs] = useState([]);
  const [pendingPdf, setPendingPdf] = useState(null);
  const [waitingIfc, setWaitingIfc] = useState(false);
  const pageRef = useRef(null);
  const pdfBytesRef = useRef(null);
  const floors = useMemo(() => alignableStoreys(storeys), [storeys]);

  useEffect(() => {
    if (!open) return undefined;
    setStep("pick");
    setPairs([]);
    setPendingPdf(null);
    setWaitingIfc(false);
    setStoreyId("");
    setDrawingId(initialDrawingId || "");
    setOpacity(0.72);
    setRasterizing(false);
    if (Array.isArray(drawingsProp) && drawingsProp.length) {
      setDrawings(drawingsProp);
      return undefined;
    }
    if (!projectId) return undefined;
    let cancelled = false;
    setDrawingsLoading(true);
    fetchProjectDrawings(projectId)
      .then((rows) => {
        if (!cancelled) setDrawings(rows);
      })
      .catch(() => {
        if (!cancelled) setDrawings([]);
      })
      .finally(() => {
        if (!cancelled) setDrawingsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, projectId, drawingsProp, initialDrawingId]);

  useEffect(() => {
    if (!open || !drawingId) {
      setPdfUrl(null);
      pdfBytesRef.current = null;
      return undefined;
    }
    let revoked = null;
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`/drawings/${drawingId}`, {
          params: { version_id: "live" },
        });
        const fileUrl = await resolveDrawingPdfUrl(response.data);
        if (cancelled) return;
        if (!fileUrl) {
          pdfBytesRef.current = null;
          setPdfUrl(null);
          setPdfError(t("ifc_viewer.link_drawing_pdf_missing"));
          return;
        }
        const bytes = await fetchPdfBytes(fileUrl);
        if (cancelled) return;
        pdfBytesRef.current = bytes;
        const blobUrl = URL.createObjectURL(
          new Blob([bytes], { type: "application/pdf" }),
        );
        revoked = blobUrl;
        setPdfUrl(blobUrl);
        setPdfError(null);
      } catch (err) {
        if (!cancelled) {
          pdfBytesRef.current = null;
          setPdfUrl(null);
          setPdfError(t(linkDrawingPdfErrorKey(err?.kind || "fetch")));
        }
      }
    })();
    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [open, drawingId, t]);

  const selectedStorey = floors.find((row) => String(row.id) === String(storeyId));

  const startOverlay = async () => {
    if (!drawingId || !storeyId || !pdfBytesRef.current) return;
    onStoreyPicked?.(selectedStorey);
    setRasterizing(true);
    try {
      const raster = await rasterizePdfPage(pdfBytesRef.current);
      setOverlayPage({ width: raster.pageWidth, height: raster.pageHeight });
      onOverlayStart?.({
        imageDataUrl: raster.imageDataUrl,
        pageWidth: raster.pageWidth,
        pageHeight: raster.pageHeight,
        elevation: Number.isFinite(selectedStorey?.elevation)
          ? selectedStorey.elevation
          : 0,
        opacity,
      });
      setStep("overlay");
    } catch (err) {
      onOverlayClear?.();
      setPdfError(t(linkDrawingPdfErrorKey(err?.kind || "raster")));
    } finally {
      setRasterizing(false);
    }
  };

  const handlePdfClick = (event) => {
    if (waitingIfc || pairs.length >= 2) return;
    const node = pageRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const x = ((event.clientX - rect.left) / rect.width) * pageSize.width;
    const y = ((event.clientY - rect.top) / rect.height) * pageSize.height;
    setPendingPdf({ x, y });
    setWaitingIfc(true);
    onIfcPointWait?.((point) => {
      setPairs((current) => {
        if (current.length >= 2) return current;
        return [...current, { pdf: { x, y }, ifc: { x: point.x, y: point.y } }];
      });
      setPendingPdf(null);
      setWaitingIfc(false);
    });
  };

  const overlayReady =
    overlayTransform &&
    Number.isFinite(overlayTransform.scale) &&
    overlayTransform.scale > 0;
  const pairsReady = pairs.length === 2 && drawingId && selectedStorey;
  const canSave =
    drawingId &&
    selectedStorey &&
    (step === "overlay" ? overlayReady : pairsReady);

  const handleSave = () => {
    if (!canSave) return;
    if (step === "overlay") {
      onSave({
        drawingId,
        storey: selectedStorey,
        control_points: overlayToControlPoints(
          overlayTransform,
          overlayPage.width,
          overlayPage.height,
        ),
      });
      return;
    }
    onSave({
      drawingId,
      storey: selectedStorey,
      control_points: pairs,
    });
  };

  const handleClose = () => {
    onIfcPointWait?.(null);
    onOverlayClear?.();
    onClose?.();
  };

  const backToPick = () => {
    onIfcPointWait?.(null);
    onOverlayClear?.();
    setStep("pick");
    setWaitingIfc(false);
    setPendingPdf(null);
    setPairs([]);
  };

  if (!open) return null;

  return (
    <div
      className="pointer-events-none fixed inset-y-0 left-0 z-[55] flex w-full max-w-md p-3"
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="false"
        aria-label={t("ifc_viewer.link_drawing_title")}
        className="pointer-events-auto flex max-h-full w-full flex-col overflow-hidden rounded-xl border border-[#e5e8ec] bg-white shadow-xl"
      >
        <div className="flex items-center justify-between border-b border-[#e5e8ec] px-4 py-3">
          <h2 className="text-sm font-semibold text-[#14171c]">
            {t("ifc_viewer.link_drawing_title")}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            aria-label={t("Cancel")}
            className="grid h-8 w-8 place-items-center rounded-lg text-[#59616d] hover:bg-[#eef0f3]"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3 text-[13px] text-[#59616d]">
          {step === "pick" ? (
            <>
              <p>{t("ifc_viewer.link_drawing_pick_help")}</p>
              <label className="block">
                <span className="mb-1 block text-[11px] font-semibold text-[#59616d]">
                  {t("ifc_viewer.link_drawing_drawing")}
                </span>
                <select
                  value={drawingId}
                  onChange={(event) => setDrawingId(event.target.value)}
                  disabled={drawingsLoading}
                  className="w-full rounded-lg border border-[#e5e8ec] px-3 py-2 text-[#14171c] outline-none focus:border-[#2b6ef0]"
                >
                  <option value="">
                    {drawingsLoading
                      ? t("Loading...")
                      : t("ifc_viewer.link_drawing_pick_drawing")}
                  </option>
                  {drawings.map((drawing) => (
                    <option key={drawing.id} value={drawing.id}>
                      {drawing.title || drawing.name || drawing.id}
                    </option>
                  ))}
                </select>
              </label>
              <div>
                <span className="mb-1 block text-[11px] font-semibold text-[#59616d]">
                  {t("ifc_viewer.link_drawing_storey")}
                </span>
                {floors.length === 0 ? (
                  <p className="m-0 text-[12px]">{t("ifc_viewer.link_drawing_no_storeys")}</p>
                ) : (
                  <div className="grid max-h-56 grid-cols-1 gap-1 overflow-y-auto">
                    {floors.map((storey) => (
                      <button
                        key={storey.id}
                        type="button"
                        onClick={() => setStoreyId(storey.id)}
                        className={`rounded-lg border px-3 py-2 text-left text-[13px] font-semibold ${
                          String(storeyId) === String(storey.id)
                            ? "border-[#2b6ef0] bg-[#eaf1fe] text-[#1d4fb0]"
                            : "border-[#e5e8ec] bg-white text-[#14171c] hover:bg-[#f6f7f9]"
                        }`}
                      >
                        {storey.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {pdfError && <p className="m-0 text-[#DC2626]">{pdfError}</p>}
            </>
          ) : step === "overlay" ? (
            <>
              <p>{t("ifc_viewer.link_drawing_overlay_help")}</p>
              <p className="m-0 text-[11px] text-[#969da8]">
                {t("ifc_viewer.link_drawing_overlay_note")}
              </p>
              <label className="block">
                <span className="mb-1 block text-[11px] font-semibold text-[#59616d]">
                  {t("ifc_viewer.link_drawing_opacity")}
                </span>
                <input
                  type="range"
                  min="0.2"
                  max="1"
                  step="0.05"
                  value={opacity}
                  onChange={(event) => {
                    const next = Number(event.target.value);
                    setOpacity(next);
                    onOverlayUpdate?.({ opacity: next });
                  }}
                  className="w-full"
                />
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() =>
                    onOverlayUpdate?.({
                      transform: overlayTransform
                        ? { ...overlayTransform, rotation: overlayTransform.rotation - 90 }
                        : { rotation: -90 },
                    })
                  }
                  className="rounded-lg border border-[#e5e8ec] px-3 py-2 text-sm font-semibold text-[#14171c] hover:bg-[#f6f7f9]"
                >
                  {t("ifc_viewer.link_drawing_rotate_left")}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    onOverlayUpdate?.({
                      transform: overlayTransform
                        ? { ...overlayTransform, rotation: overlayTransform.rotation + 90 }
                        : { rotation: 90 },
                    })
                  }
                  className="rounded-lg border border-[#e5e8ec] px-3 py-2 text-sm font-semibold text-[#14171c] hover:bg-[#f6f7f9]"
                >
                  {t("ifc_viewer.link_drawing_rotate_right")}
                </button>
                <button
                  type="button"
                  onClick={() => onOverlayUpdate?.({ transform: null })}
                  className="rounded-lg border border-[#e5e8ec] px-3 py-2 text-sm font-semibold text-[#14171c] hover:bg-[#f6f7f9]"
                >
                  {t("ifc_viewer.link_drawing_reset")}
                </button>
              </div>
                <button
                  type="button"
                  onClick={() => {
                    onOverlayClear?.({ restoreView: false });
                    setStep("calibrate");
                    setPairs([]);
                    setPendingPdf(null);
                    setWaitingIfc(false);
                  }}
                className="text-left text-[12px] font-semibold text-[#1d4fb0] underline-offset-2 hover:underline"
              >
                {t("ifc_viewer.link_drawing_advanced")}
              </button>
            </>
          ) : (
            <>
              <p>
                {waitingIfc
                  ? t("ifc_viewer.link_drawing_click_ifc", {
                      number: pairs.length + 1,
                    })
                  : t("ifc_viewer.link_drawing_click_pdf", {
                      number: pairs.length + 1,
                    })}
              </p>
              <p className="m-0 text-[11px] text-[#969da8]">
                {t("ifc_viewer.link_drawing_v1_note")}
              </p>
              {pdfError && <p className="m-0 text-[#DC2626]">{pdfError}</p>}
              {pdfUrl && (
                <div
                  ref={pageRef}
                  onClick={handlePdfClick}
                  className={`overflow-auto rounded-lg border border-[#e5e8ec] bg-[#f6f7f9] ${
                    waitingIfc ? "cursor-default opacity-70" : "cursor-crosshair"
                  }`}
                >
                  <Document file={pdfUrl} loading={t("Loading...")}>
                    <Page
                      pageNumber={1}
                      width={360}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      onLoadSuccess={(page) =>
                        setPageSize({
                          width: page.originalWidth || page.width || 1,
                          height: page.originalHeight || page.height || 1,
                        })
                      }
                    />
                  </Document>
                </div>
              )}
              <ol className="m-0 list-decimal space-y-1 pl-5 text-[12px] text-[#14171c]">
                {pairs.map((pair, index) => (
                  <li key={`${pair.pdf.x}-${pair.ifc.x}-${index}`}>
                    {t("ifc_viewer.link_drawing_pair", {
                      number: index + 1,
                      pdfX: Math.round(pair.pdf.x),
                      pdfY: Math.round(pair.pdf.y),
                      ifcX: pair.ifc.x.toFixed(2),
                      ifcY: pair.ifc.y.toFixed(2),
                    })}
                  </li>
                ))}
                {pendingPdf && (
                  <li>
                    {t("ifc_viewer.link_drawing_pdf_pending", {
                      number: pairs.length + 1,
                      x: Math.round(pendingPdf.x),
                      y: Math.round(pendingPdf.y),
                    })}
                  </li>
                )}
              </ol>
              <button
                type="button"
                onClick={() => {
                  onIfcPointWait?.(null);
                  startOverlay();
                }}
                className="text-left text-[12px] font-semibold text-[#1d4fb0] underline-offset-2 hover:underline"
              >
                {t("ifc_viewer.link_drawing_back_overlay")}
              </button>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-[#e5e8ec] px-4 py-3">
          {step !== "pick" && (
            <button
              type="button"
              onClick={backToPick}
              className="rounded-lg border border-[#e5e8ec] px-3 py-2 text-sm font-semibold text-[#14171c] hover:bg-[#f6f7f9]"
            >
              {t("Back")}
            </button>
          )}
          <button
            type="button"
            onClick={handleClose}
            className="rounded-lg border border-[#e5e8ec] px-3 py-2 text-sm font-semibold text-[#14171c] hover:bg-[#f6f7f9]"
          >
            {t("Cancel")}
          </button>
          {step === "pick" ? (
            <button
              type="button"
              disabled={!drawingId || !storeyId || !pdfUrl || rasterizing}
              onClick={startOverlay}
              className="rounded-lg bg-[#2b6ef0] px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {rasterizing
                ? t("Loading...")
                : t("ifc_viewer.link_drawing_continue")}
            </button>
          ) : (
            <button
              type="button"
              disabled={!canSave || saving}
              onClick={handleSave}
              className="rounded-lg bg-[#2b6ef0] px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {saving ? t("Saving...") : t("ifc_viewer.link_drawing_save")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
